import axios from "axios";
import { put, takeEvery, takeLatest, select, SelectEffect } from "redux-saga/effects";
import { RootState } from "reducers";
import { showError } from "../components/commonUtilities";

import {
    ADD_COMMENT_TO_WORK_ORDER,
    AddCommentToWorkOrderAction,
    CREATE_WORK_ORDER,
    CreateWorkOrderAction,
    DELETE_WORK_ORDERS,
    DeleteWorkOrdersAction,
    GET_WORK_ORDERS,
    GET_WORK_ORDERS_TABLE_FILTERS,
    GetWorkOrdersAction,
    ListViewFiltersType,
    ReceivedWorkOrdersTableFiltersAction,
    UPDATE_WORK_ORDER_STATUS,
    UpdateWorkOrderStatusAction,
    WorkOrdersReceivedAction
} from "../actions/WorkOrders/types";
import {
    getWorkOrders,
    receivedWorkOrdersTableFilters,
    toggleWorkOrderActionPerformed,
    workOrdersReceived
} from "../actions";
import { GetRequestSingleItemInterface } from "../interfaces/ActionReducer/GetMethod/GetRequestSingleItem";
import { GeneratorYieldType } from "../interfaces/ActionReducer/GetMethod/GetResponse";
import { showToast } from "components/DesignSystem";

const workOrdersParams = (state: RootState) => state.WorkOrders.workOrdersParams;

function* getWorkOrdersTableFilters(): Generator<
    | Promise<ReceivedWorkOrdersTableFiltersAction>
    | GeneratorYieldType<ReceivedWorkOrdersTableFiltersAction["payload"]>,
    void,
    ReceivedWorkOrdersTableFiltersAction["payload"]["data"]
> {
    try {
        const json = yield axios
            .get("/api/work-order/workorder_table_config/")
            .then((res) => res.data);

        yield put(receivedWorkOrdersTableFilters({ data: json }));
    } catch (err) {
        showError(err);
    }
}

export function* getWorkOrdersTableFiltersSaga() {
    yield takeLatest(GET_WORK_ORDERS_TABLE_FILTERS, getWorkOrdersTableFilters);
}

function* getWorkOrdersList(
    action: GetWorkOrdersAction
): Generator<
    | Generator<
          | Promise<ReceivedWorkOrdersTableFiltersAction>
          | GeneratorYieldType<{ data: ListViewFiltersType[] }>,
          void,
          ListViewFiltersType[]
      >
    | Promise<WorkOrdersReceivedAction>
    | GeneratorYieldType<WorkOrdersReceivedAction["payload"]>,
    void,
    WorkOrdersReceivedAction["payload"]["workOrdersList"]
> {
    let url = "/api/work-order/?";

    Object.keys(action.payload.params).forEach((prop: string) => {
        if (Array.isArray(action.payload.params[prop])) {
            if (action?.payload?.params[prop]) {
                let allFilters = action.payload.params[prop]?.toString();
                url += `&${prop}=${allFilters}`;
            }
        } else {
            url += `&${prop}=${action.payload.params[prop]}`;
        }
    });

    try {
        const json = yield axios.get(url).then((res) => res.data);

        yield put(workOrdersReceived({ workOrdersList: json }));
        yield getWorkOrdersTableFilters();
    } catch (err) {
        showError(err);
    }
}

export function* getWorkOrderSaga() {
    yield takeLatest(GET_WORK_ORDERS, getWorkOrdersList);
}

function* createWorkOrders(
    action: CreateWorkOrderAction
): Generator<
    | Promise<Partial<GetRequestSingleItemInterface>>
    | SelectEffect
    | GeneratorYieldType<GetWorkOrdersAction["payload"]>,
    void,
    GetWorkOrdersAction["payload"]["params"]
> {
    try {
        const readFile = (file: File): Promise<any> =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = (): void => {
                    // Extract the base64 string
                    const base64String = reader.result;

                    resolve(base64String);
                };

                reader.onerror = (): void => {
                    reject(new Error("Error reading file."));
                };

                reader.readAsDataURL(file);
            });
        const { file, name } = action.payload.params;
        const iterator = readFile(file);

        const base64String = yield iterator;

        const payload = new FormData();

        payload.append("name", name);
        payload.append("file", base64String as any);

        yield axios
            .post("/api/work-order/", payload, {
                headers: { "Content-Type": "multipart/form-data;" }
            })
            .then((res) => res.data);

        const params = yield select(workOrdersParams);

        yield put(getWorkOrders({ params }));
        showToast("Work Orders has been Imported Successfully", { type: "success" });
    } catch (err) {
        showError(err);
    }
}

export function* createWorkOrderSaga() {
    yield takeLatest(CREATE_WORK_ORDER, createWorkOrders);
}

function* updateWorkOrderStatus(action: UpdateWorkOrderStatusAction) {
    try {
        yield axios
            .patch(`/api/work-order/${action.payload.id}/`, action.payload.params)
            .then((response) => response.data);

        yield put(toggleWorkOrderActionPerformed());
        showToast("Status has been changed successfully", { type: "success" });
    } catch (error) {
        showError(error);
    }
}

export function* updateWorkOrderStatusSaga() {
    yield takeEvery(UPDATE_WORK_ORDER_STATUS, updateWorkOrderStatus);
}

function* deleteWorkOrders(action: DeleteWorkOrdersAction) {
    try {
        yield Promise.all(
            action.payload.ids.map((id, index) => {
                return axios.delete(`api/work-order/${id}/`).then((response) => {
                    if (index === action.payload.ids.length - 1) {
                        if (action.payload.afterDelete) {
                            action.payload.afterDelete();
                        }
                    }
                    return response.data;
                });
            })
        ).catch((e) => showError(e));
    } catch (error) {
        showError(error);
    }
}

export function* deleteWorkOrdersSaga() {
    yield takeLatest(DELETE_WORK_ORDERS, deleteWorkOrders);
}

function* addCommentToWorkOrder(
    action: AddCommentToWorkOrderAction
): Generator<
    | Promise<GetWorkOrdersAction["payload"]>
    | GeneratorYieldType<GetWorkOrdersAction["payload"]>
    | SelectEffect,
    void,
    GetWorkOrdersAction["payload"]["params"]
> {
    try {
        yield axios.post(`/api/work-order-comments/`, action.payload.params).then((response) => {
            return response.data;
        });

        const params = yield select(workOrdersParams);

        yield put(getWorkOrders({ params }));
        showToast("Feedback has been submitted successfully", { type: "success" });
    } catch (error) {
        showError(error);
    }
}

export function* addCommentToWorkOrderSaga() {
    yield takeEvery(ADD_COMMENT_TO_WORK_ORDER, addCommentToWorkOrder);
}
